import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import {
  vehicleRequestService,
  VehicleRequest,
  VehicleRequestStatus,
  VehicleRequestType,
} from '../../api/services/vehicleRequestService';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Filters {
  status: VehicleRequestStatus | 'all';
  type: VehicleRequestType | 'all';
  search: string;
}

export const useVehicleRequests = () => {
  const [requests, setRequests] = useState<VehicleRequest[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [filters, setFilters] = useState<Filters>({
    status: 'pending',
    type: 'all',
    search: '',
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // ── Detail / reject modal ──────────────────────────────────────────
  const [selectedRequest, setSelectedRequest] = useState<VehicleRequest | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vehicleRequestService.getQueue({
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status === 'all' ? undefined : filters.status,
        type: filters.type === 'all' ? undefined : filters.type,
        search: filters.search || undefined,
      });
      setRequests(res.data ?? []);
      setPagination(prev => ({ ...prev, ...(res.pagination ?? {}) }));
    } catch {
      message.error('Failed to load vehicle requests');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // ── Actions ───────────────────────────────────────────────────────

  const handleApprove = async (id: string) => {
    setActionLoading(true);
    try {
      await vehicleRequestService.approve(id);
      message.success('Request approved — item added to the database.');
      fetchQueue();
    } catch {
      message.error('Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (req: VehicleRequest) => {
    setSelectedRequest(req);
    setRejectionReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await vehicleRequestService.reject(selectedRequest.id, rejectionReason);
      message.success('Request rejected.');
      setRejectModalOpen(false);
      setSelectedRequest(null);
      setRejectionReason('');
      fetchQueue();
    } catch {
      message.error('Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTableChange = (newPage: number, newLimit: number) => {
    setPagination(prev => ({ ...prev, page: newPage, limit: newLimit }));
  };

  const handleFilterChange = (patch: Partial<Filters>) => {
    setFilters(prev => ({ ...prev, ...patch }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return {
    requests,
    pagination,
    filters,
    loading,
    actionLoading,
    selectedRequest,
    rejectModalOpen,
    rejectionReason,
    setRejectionReason,
    fetchQueue,
    handleApprove,
    openRejectModal,
    handleReject,
    handleTableChange,
    handleFilterChange,
    setRejectModalOpen,
  };
};
